import { Info } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/Popover'

type SyncStrategy = 'full' | 'snapshot' | 'update_only' | 'incremental'

const SYNC_INFO: Record<SyncStrategy, { title: string; description: string }> = {
  full: {
    title: 'Full Sync',
    description:
      'Pulls every row from the question and upserts it by ID. Existing rows are updated, new rows added, nothing deleted. Best for tables with a stable ID that change over time.',
  },
  snapshot: {
    title: 'Snapshot Sync',
    description:
      "Replaces the whole table each run: clears this org's rows, then inserts the fresh pull. For sources with no stable row ID. If the query returns 0 rows, the replace is skipped to protect existing data.",
  },
  update_only: {
    title: 'Update Only',
    description:
      'Fetches all rows but only updates ones already in the table (matched by ID) — never inserts or deletes. Used to enrich a table another source owns. Unmatched rows are skipped.',
  },
  incremental: {
    title: 'Incremental Sync',
    description:
      'Syncs one day at a time from the last synced date up to today (up to 90 days per run, continuing automatically). Best for large, append-heavy time-series like daily sales or stock. Requires a date parameter in the question.',
  },
}

export default function SyncSectionHeader({ strategy }: { strategy: SyncStrategy }) {
  const info = SYNC_INFO[strategy]

  return (
    <div className="flex items-center gap-1">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
        {info.title}
      </h2>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
            aria-label={`About ${info.title}`}
          >
            <Info size={14} />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72">
          <div className="flex flex-col gap-1.5">
            <p className="text-sm font-semibold text-foreground">{info.title}</p>
            <p className="text-xs text-muted-foreground leading-relaxed">{info.description}</p>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
